import { useId, useState } from 'react';
import { SmartImage } from '@/components/common/SmartImage';
import type { MediaRef } from '@cng/shared';

interface BeforeAfterSliderProps {
  beforeImage: MediaRef | null;
  afterImage: MediaRef | null;
  labelBefore: string;
  labelAfter: string;
  stylistBadge?: string;
  initialPosition?: number;
  title: string;
}

/**
 * Draggable before/after comparison.
 *
 * The original overlays a transparent `<input type="range">` on the images,
 * which is exactly right: it gives pointer, touch and keyboard control from one
 * native control. That approach is kept, with the labelling a range input needs
 * to be usable by a screen reader added on top.
 */
export function BeforeAfterSlider({
  beforeImage, afterImage, labelBefore, labelAfter,
  stylistBadge, initialPosition = 50, title,
}: BeforeAfterSliderProps) {
  const [position, setPosition] = useState(initialPosition);
  const id = useId();

  return (
    <div className="ba-slider">
      {labelBefore ? <div className="label label-before">{labelBefore}</div> : null}
      {labelAfter ? <div className="label label-after">{labelAfter}</div> : null}
      {stylistBadge ? <div className="stylist-badge">{stylistBadge}</div> : null}

      <div className="ba-image-container img-after">
        <SmartImage
          media={afterImage}
          alt={afterImage?.alt || `${title} — after`}
          sizes="(max-width: 768px) 100vw, 560px"
        />
      </div>

      <div className="ba-image-container img-before" style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}>
        <SmartImage
          media={beforeImage}
          alt={beforeImage?.alt || `${title} — before`}
          sizes="(max-width: 768px) 100vw, 560px"
        />
      </div>

      <label className="sr-only" htmlFor={id}>
        {`${title}: drag to compare before and after`}
      </label>
      <input
        id={id}
        type="range"
        min={0}
        max={100}
        step={1}
        value={position}
        onChange={(e) => setPosition(Number(e.target.value))}
        aria-valuetext={`${position}% of the before photo shown`}
      />

      <div className="slider-handle" style={{ left: `${position}%` }} aria-hidden="true" />
    </div>
  );
}
